import { ExternalLink } from "lucide-react";

/**
 * Delivery-form evidence cards.
 *
 * IMPORTANT — claims policy:
 * Every numeric "absorption %" previously shown here was audited against the
 * paper it cited. The percentages were not reported in those papers (and in
 * several cases the PubMed IDs pointed at unrelated research). They have been
 * removed rather than re-guessed. What remains is a plain, sourced statement
 * about the delivery form, attributed to the studied formulation — never to
 * TruWe's finished product. Do not add a percentage here unless it is quoted
 * verbatim from the linked paper, with the measurement type stated.
 */

export type AbsorptionRow = {
  /** Ingredient name shown on the card. */
  label: string;
  /** The form TruWe uses. */
  truweForm: string;
  /** The commodity form it is being contrasted with. */
  standardForm: string;
  /** What the linked study actually found, in plain words. */
  evidence: string;
  /** What was measured (urinary excretion, plasma AUC, in-vitro survival...). */
  measure: string;
  /** Verified source, or null when no paper supports a comparison. */
  source: { label: string; url: string } | null;
};

/** Verified citations only. Each was checked against the PubMed record. */
const SRC = {
  firoz: {
    label: "Firoz M, Graber M. Magnes. Res. 2001 (US magnesium preparations)",
    url: "https://pubmed.ncbi.nlm.nih.gov/11794633/",
  },
  walker: {
    label: "Walker AF et al., Magnes. Res. 2003 (citrate vs chelate vs oxide)",
    url: "https://pubmed.ncbi.nlm.nih.gov/14596323/",
  },
  aquamin: {
    label: "Felice VD et al., Nutrients 2018 (Aquamin-Magnesium)",
    url: "https://pubmed.ncbi.nlm.nih.gov/30018220/",
  },
  curcuminPhospholipid: {
    label: "Cuomo J et al., J. Nat. Prod. 2011 (phospholipid curcumin vs unformulated)",
    url: "https://pubmed.ncbi.nlm.nih.gov/21495671/",
  },
  silymarin: {
    label: "Di Costanzo A, Angelico R. Molecules 2019 (silymarin bioavailability review)",
    url: "https://pubmed.ncbi.nlm.nih.gov/30717170/",
  },
  folate: {
    label: "Pietrzik K et al., Clin. Pharmacokinet. 2010 (folic acid vs L-5-MTHF)",
    url: "https://pubmed.ncbi.nlm.nih.gov/20608755/",
  },
  nacRedox: {
    label: "Schmitt B et al., Redox Biol. 2015 (NAC and glutathione markers)",
    url: "https://pubmed.ncbi.nlm.nih.gov/26393691/",
  },
  probiotics: {
    label: "Govender M et al., AAPS PharmSciTech 2013 (probiotic delivery review)",
    url: "https://pubmed.ncbi.nlm.nih.gov/24222267/",
  },
  inositolRatio: {
    label: "Nordio M et al., Eur. Rev. Med. Pharmacol. Sci. 2019 (40:1 myo:D-chiro ratio)",
    url: "https://pubmed.ncbi.nlm.nih.gov/31298405/",
  },
  chromium: {
    label: "DiSilvestro RA, Dy E. J. Trace Elem. Med. Biol. 2007 (chromium forms)",
    url: "https://pubmed.ncbi.nlm.nih.gov/17499152/",
  },
};

const MG_OXIDE_EVIDENCE =
  "In a controlled comparison of commercial magnesium preparations, magnesium oxide was the least absorbed form, while organic salts such as chloride, lactate and aspartate were absorbed several times better.";

const ROW_MG_CHLORIDE: AbsorptionRow = {
  label: "Mg Chloride",
  truweForm: "Magnesium chloride (ionised)",
  standardForm: "Magnesium oxide",
  evidence: MG_OXIDE_EVIDENCE,
  measure: "24-hour urinary magnesium excretion in healthy adults",
  source: SRC.firoz,
};

const DEFAULT_ROWS: AbsorptionRow[] = [
  {
    label: "Curcumin",
    truweForm: "Liposomal (phospholipid) curcumin",
    standardForm: "Standard 95% curcuminoids",
    evidence:
      "Unformulated curcumin is poorly absorbed. A phospholipid-delivered curcumin was studied against unformulated curcuminoids and produced substantially higher plasma exposure. The study used a phytosome, not TruWe's liposome, so it describes the delivery principle rather than this product.",
    measure: "Plasma AUC in healthy volunteers",
    source: SRC.curcuminPhospholipid,
  },
  {
    label: "Silymarin",
    truweForm: "Liposomal silymarin",
    standardForm: "Standard milk thistle extract",
    evidence:
      "Silymarin is poorly water-soluble and poorly absorbed from standard extracts; lipid-based carriers are the main strategy studied to improve it. Published work is formulation research, not a human trial of this product.",
    measure: "Formulation/bioavailability review",
    source: SRC.silymarin,
  },
  {
    label: "Alpha-Lipoic Acid",
    truweForm: "Liposomal alpha-lipoic acid",
    standardForm: "Free-form ALA",
    evidence:
      "Free-form ALA is unstable and rapidly cleared. We have not found a human study comparing a liposomal ALA with free-form ALA, so no absorption advantage is claimed for this ingredient.",
    measure: "No supporting human comparison identified",
    source: null,
  },
  {
    label: "Magnesium",
    truweForm: "Bisglycinate + Aquamin® marine magnesium",
    standardForm: "Magnesium oxide",
    evidence: MG_OXIDE_EVIDENCE,
    measure: "24-hour urinary magnesium excretion in healthy adults",
    source: SRC.firoz,
  },
  {
    label: "Folate",
    truweForm: "Quatrefolic® L-5-MTHF",
    standardForm: "Folic acid",
    evidence:
      "L-5-MTHF is the already-active form and does not need enzymatic reduction, which matters for people with reduced MTHFR activity. The cited review reports the two forms as broadly comparable in bioavailability, so we make no absorption-advantage claim.",
    measure: "Comparative pharmacokinetics review",
    source: SRC.folate,
  },
  {
    label: "Probiotics",
    truweForm: "Acid-protected delivery",
    standardForm: "Unprotected strains",
    evidence:
      "Stomach acid reduces the number of live organisms reaching the intestine; encapsulation is the standard approach for protecting them. The cited work is a review of delivery methods, not a survival measurement for this product.",
    measure: "Delivery-technology review",
    source: SRC.probiotics,
  },
];

export const PRODUCT_ABSORPTION_ROWS: Record<"liver" | "mag" | "ova", AbsorptionRow[]> = {
  liver: [
    {
      label: "NAC",
      truweForm: "N-Acetyl Cysteine 600 mg",
      standardForm: "Lower-dose or absent NAC",
      evidence:
        "NAC is a precursor your body uses to make glutathione; the cited crossover study measured oxidative-stress markers.",
      measure: "Redox biomarkers in a crossover study",
      source: SRC.nacRedox,
    },
    DEFAULT_ROWS[1]!, // Silymarin
    DEFAULT_ROWS[2]!, // ALA
    DEFAULT_ROWS[0]!, // Curcumin
    DEFAULT_ROWS[5]!, // Probiotics
  ],
  mag: [
    ROW_MG_CHLORIDE,
    {
      ...ROW_MG_CHLORIDE,
      label: "Mg Gluconate",
      truweForm: "Magnesium gluconate",
    },
    {
      label: "Aquamin®",
      truweForm: "Aquamin® marine multi-mineral magnesium",
      standardForm: "Magnesium oxide",
      evidence:
        "Aquamin-Magnesium has its own published bioaccessibility and bioavailability work supporting that the marine multi-mineral form is absorbed and utilised.",
      measure: "Bioaccessibility / bioavailability study",
      source: SRC.aquamin,
    },
    {
      ...ROW_MG_CHLORIDE,
      label: "Mg Aspartate",
      truweForm: "Magnesium aspartate",
    },
    {
      label: "Mg Bisglycinate",
      truweForm: "Liposomal magnesium bisglycinate",
      standardForm: "Magnesium oxide",
      evidence:
        "In a randomised double-blind comparison, magnesium oxide performed worst of the preparations tested; organic and chelated forms performed better (citrate best in that trial). No published figure exists for a liposomal bisglycinate, so none is quoted.",
      measure: "Randomised double-blind supplementation trial",
      source: SRC.walker,
    },
  ],
  ova: [
    {
      label: "Inositol 40:1",
      truweForm: "Myo-inositol : D-chiro-inositol 40:1",
      standardForm: "Myo-inositol alone",
      evidence:
        "The 40:1 ratio mirrors the physiological plasma ratio and is the ratio used in published PCOS ovulation research. This is about ratio, not absorption.",
      measure: "Clinical study of the 40:1 plasma ratio",
      source: SRC.inositolRatio,
    },
    {
      label: "Berberine",
      truweForm: "Berberine HCl with Bio-Absorb Matrix",
      standardForm: "Standard berberine HCl",
      evidence:
        "Berberine is known to be poorly absorbed orally. We have not found a human study quantifying an absorption gain for berberine combined with our blend, so no figure or advantage is claimed.",
      measure: "No supporting human comparison identified",
      source: null,
    },
    DEFAULT_ROWS[4]!, // Folate
    {
      label: "Magnesium",
      truweForm: "Magnesium bisglycinate",
      standardForm: "Magnesium oxide",
      evidence: MG_OXIDE_EVIDENCE,
      measure: "24-hour urinary magnesium excretion in healthy adults",
      source: SRC.firoz,
    },
    {
      label: "Chromium",
      truweForm: "Chromium picolinate",
      standardForm: "Chromium chloride",
      evidence:
        "An acute comparison of commercially available chromium supplements found absorption differs by form, with picolinate absorbed better than chloride.",
      measure: "24-hour urinary chromium after a single dose",
      source: SRC.chromium,
    },
  ],
};

export function AbsorptionRateChart({
  className = "",
  rows = DEFAULT_ROWS,
  eyebrow = "Delivery forms and the published evidence",
  title = "What the research actually says about each delivery form.",
  description = "For every active we list the form we use, the commodity form it is usually compared with, and what the linked paper measured. Where no study supports a comparison, we say so instead of estimating.",
}: {
  className?: string;
  rows?: AbsorptionRow[];
  eyebrow?: string;
  title?: string;
  description?: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-[2rem] border border-border bg-brand-soft/40 p-5 md:p-8 ${className}`}
    >
      <div>
        <p className="font-display text-[11px] font-bold uppercase tracking-[0.28em] text-brand">
          {eyebrow}
        </p>
        <h3 className="mt-3 font-display text-2xl font-bold leading-[1.05] tracking-tight text-brand-deep md:text-4xl">
          {title}
        </h3>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>

      <ul className="mt-8 grid gap-4 border-t border-border/60 pt-8 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <li
            key={`${r.label}-${r.truweForm}`}
            className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-[0_1px_2px_rgba(44,44,44,0.04)] transition-shadow hover:shadow-[0_8px_24px_rgba(44,44,44,0.08)]"
          >
            <p className="font-display text-base font-semibold leading-snug text-brand-deep">
              {r.label}
            </p>
            <p className="mt-3 text-sm font-semibold text-brand-deep">{r.truweForm}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              usually compared with {r.standardForm}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground/80">{r.evidence}</p>
            <p className="mt-3 text-xs text-muted-foreground">Measured: {r.measure}</p>
            {r.source ? (
              <a
                href={r.source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-start gap-1 text-xs text-muted-foreground transition-colors hover:text-brand"
              >
                <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                <span>{r.source.label}</span>
              </a>
            ) : (
              <p className="mt-2 text-xs italic text-muted-foreground">
                No published human comparison — no claim made.
              </p>
            )}
          </li>
        ))}
      </ul>

      <p className="mt-6 text-[10px] leading-relaxed text-muted-foreground">
        These statements describe published research on individual ingredients and their delivery
        forms. They are not evidence about TruWe&apos;s finished formulations, and the doses used in
        studies are not necessarily the doses on our label. Food supplement, not a medicine.
      </p>
    </div>
  );
}
